'use client';

import { useCallback, useState } from 'react';
import { DEFAULT_REVIEWS_LIMIT, CacheKeys, ReviewsSortingOption, ServerActionName } from '@headless-workspace/config';
import { useSafeSWRMutation } from '@headless-workspace/domain/common/client';
import { getReviewsWithSortingServerAction, ProductReview, RatingsAndReviewsValue } from '../../../../../src/server';
import { filterReviewsLocally } from './filterReviewsLocally';

type UseFilteredReviewsProps = {
    baseReviews: ProductReview[];
    reviewCount: number;
    productId: string;
};

type UseFilteredReviewsValue = {
    onRatingChange: (rating: number) => Promise<void>;
    onResetSelectedRatings: () => Promise<void>;
    onOptionUpdate: (value: string) => Promise<void>;
    onSeeMoreReviewsClick: () => Promise<void>;
    selectedRatings: number[];
    displayedReviews: number;
    reviews: ProductReview[];
    isMutating: boolean;
};

const MORE_REVIEWS_BY_FETCH = 4;
const FILTER_FETCH_OFFSET = 0;
const DEFAULT_REVIEWS_DISPLAYED = 2;

export const useFilteredReviews = ({
    baseReviews,
    reviewCount,
    productId,
}: UseFilteredReviewsProps): UseFilteredReviewsValue => {
    const [selectedRatings, setSelectedRatings] = useState<number[]>([]);
    const [filterOption, setFilterOption] = useState(ReviewsSortingOption.MostRecent);
    const [reviews, setReviews] = useState<ProductReview[]>(baseReviews);
    const [displayedReviews, setDisplayedReviews] = useState<number>(DEFAULT_REVIEWS_DISPLAYED);

    const { trigger: fetchMoreReviews, isMutating } = useSafeSWRMutation<
        RatingsAndReviewsValue,
        Parameters<typeof getReviewsWithSortingServerAction>
    >(CacheKeys.UnusedKey, ServerActionName.getReviews, (_, { arg }) => getReviewsWithSortingServerAction(...arg), {
        onSuccess: (result) => {
            if (result.type === 'success') {
                setReviews(result.data.reviews);
                setDisplayedReviews(
                    (prevDisplay) => Math.min(result.data.reviews.length, prevDisplay) || DEFAULT_REVIEWS_DISPLAYED,
                );
            }
        },
    });

    const fetchReviews = useCallback(
        async (newDisplayedReviews: number, newSelectedRatings: number[], newFilterOption: ReviewsSortingOption) => {
            const hasAllReviewsLocally = reviewCount <= DEFAULT_REVIEWS_LIMIT;

            if (hasAllReviewsLocally) {
                const filtered = filterReviewsLocally(baseReviews, newSelectedRatings, newFilterOption);
                setReviews(filtered);
                setDisplayedReviews(DEFAULT_REVIEWS_DISPLAYED);
                return;
            }

            if (!isMutating) {
                await fetchMoreReviews([
                    productId,
                    FILTER_FETCH_OFFSET,
                    newDisplayedReviews,
                    newSelectedRatings,
                    newFilterOption,
                ]);
            }
        },
        [fetchMoreReviews, productId, isMutating, reviewCount, baseReviews],
    );

    const onRatingChange = useCallback(
        async (rating: number) => {
            const newRatings = selectedRatings.includes(rating)
                ? selectedRatings.filter((r) => r !== rating)
                : [...selectedRatings, rating];

            setSelectedRatings(newRatings);

            await fetchReviews(displayedReviews, newRatings, filterOption);
        },
        [filterOption, displayedReviews, fetchReviews, selectedRatings],
    );

    const onOptionUpdate = useCallback(
        async (value: string) => {
            const sortingOption = value as ReviewsSortingOption;
            setFilterOption(sortingOption);
            void fetchReviews(displayedReviews, selectedRatings, sortingOption);
        },
        [selectedRatings, displayedReviews, fetchReviews],
    );

    const onResetSelectedRatings = useCallback(async () => {
        setSelectedRatings([]);
        void fetchReviews(displayedReviews, [], filterOption);
    }, [filterOption, displayedReviews, fetchReviews]);

    const onSeeMoreReviewsClick = useCallback(async () => {
        if (reviewCount > displayedReviews && displayedReviews + MORE_REVIEWS_BY_FETCH > reviews.length) {
            const newDisplayedReviews = Math.min(displayedReviews + MORE_REVIEWS_BY_FETCH, reviewCount);
            void fetchReviews(newDisplayedReviews, selectedRatings, filterOption);
        }

        setDisplayedReviews((prevDisplay) => Math.min(prevDisplay + MORE_REVIEWS_BY_FETCH, reviewCount));
    }, [isMutating, reviewCount, selectedRatings, filterOption, fetchReviews]);

    return {
        onRatingChange,
        onOptionUpdate,
        onResetSelectedRatings,
        onSeeMoreReviewsClick,
        selectedRatings,
        displayedReviews,
        reviews,
        isMutating,
    };
};
